'use client'
import { dismissGlobalAlert } from '@/lib/services/actions/global-alerts'
import { RootState } from '@/lib/store'
import { Alert, dismissAlert } from '@/lib/store/core/alerts'
import { addDismissedAlertCookie, getDismissedAlertsCookie } from '@/lib/utils/alert-cookies'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Chip,
  Popover,
  ScrollShadow,
  Tab,
  Tabs
} from '@heroui/react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '../ui/icon'
import { AlertDisplay } from './alert-display'

export function AlertPortalButton() {
  const dispatch = useDispatch()
  const alerts = useSelector((state: RootState) => state.alerts.alerts)
  const dismissedAlerts = useSelector((state: RootState) => state.alerts.dismissedAlerts || [])
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState('new')

  // Load dismissed alerts from cookies on mount
  useEffect(() => {
    const cookieDismissedAlerts = getDismissedAlertsCookie()
    if (cookieDismissedAlerts.length > 0) {
      // Add cookie-dismissed alerts to Redux store
      cookieDismissedAlerts.forEach((alertId) => {
        if (!dismissedAlerts.includes(alertId)) {
          dispatch(dismissAlert(alertId))
        }
      })
    }
  }, [dismissedAlerts, dispatch])

  // Filter alerts into new and dismissed
  const newAlerts = alerts.filter((alert: Alert) => !dismissedAlerts.includes(alert.id))
  const dismissedAlertItems = alerts.filter((alert: Alert) => dismissedAlerts.includes(alert.id))

  // Function to handle alert dismissal
  const handleDismiss = async (alertId: string) => {
    try {
      // Store dismissal in cookie for anonymous users
      addDismissedAlertCookie(alertId)

      // Attempt to record dismissal on server (for authenticated users)
      await dismissGlobalAlert(alertId)

      // Update Redux store
      dispatch(dismissAlert(alertId))
    } catch (error) {
      console.error('Failed to record alert dismissal:', error)
      // Even if server storage fails, keep the cookie and Redux state updated
      dispatch(dismissAlert(alertId))
    }
  }

  return (
    <Popover placement='bottom-end' offset={12} isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Trigger>
        <Button
          disableRipple
          isIconOnly
          className='overflow-visible bg-content2'
          radius='full'
          variant='ghost'
          aria-label='Announcements'
          size='sm'
        >
          <Badge.Anchor>
            {newAlerts.length > 0 && (
              <Badge color='danger' size='sm' placement='top-right'>
                {newAlerts.length}
              </Badge>
            )}
            <Icon icon='bell' className='w-4 h-4' />
          </Badge.Anchor>
        </Button>
      </Popover.Trigger>
      <Popover.Content className='max-w-[90vw] p-0 sm:max-w-[380px]'>
        <Card className='w-full shadow-none border-none'>
          <CardHeader className='flex flex-col px-0 pb-0'>
            <div className='flex w-full items-center justify-between px-5 py-2'>
              <div className='inline-flex items-center gap-1'>
                <h4 className='inline-block align-middle text-large font-medium'>Announcements</h4>
                <Chip size='sm' variant='soft'>
                  {newAlerts.length}
                </Chip>
              </div>
              <Button
                className='h-8 px-3'
                radius='full'
                variant='ghost'
                onPress={() => {
                  // Mark all new alerts as dismissed
                  newAlerts.forEach((alert) => handleDismiss(alert.id))
                }}
                isDisabled={newAlerts.length === 0}
              >
                Mark all as read
              </Button>
            </div>
            <Tabs
              aria-label='Announcement Tabs'
              classNames={{
                base: 'w-full',
                tabList: 'gap-6 px-6 py-0 w-full relative rounded-none border-b border-divider',
                cursor: 'w-full',
                tab: 'max-w-fit px-2 h-12'
              }}
              color='primary'
              selectedKey={selectedTab}
              variant='secondary'
              onSelectionChange={(selected) => setSelectedTab(selected as string)}
            >
              <Tab
                key='new'
                title={
                  <div className='flex items-center space-x-2'>
                    <span>New</span>
                    <Chip size='sm' variant='soft'>
                      {newAlerts.length}
                    </Chip>
                  </div>
                }
              />
              <Tab
                key='dismissed'
                title={
                  <div className='flex items-center space-x-2'>
                    <span>Dismissed</span>
                    <Chip size='sm' variant='soft'>
                      {dismissedAlertItems.length}
                    </Chip>
                  </div>
                }
              />
            </Tabs>
          </CardHeader>
          <CardContent className='w-full gap-0 p-0'>
            <ScrollShadow className='h-[400px] w-full'>
              {selectedTab === 'new' ? (
                newAlerts.length > 0 ? (
                  newAlerts.map((alert: Alert) => (
                    <AlertDisplay key={alert.id} alert={alert} onDismiss={handleDismiss} />
                  ))
                ) : (
                  <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
                    <Icon icon='bell' className='w-10 h-10 text-default-400' />
                    <p className='text-small text-default-400'>No new alerts</p>
                  </div>
                )
              ) : dismissedAlertItems.length > 0 ? (
                dismissedAlertItems.map((alert: Alert) => <AlertDisplay key={alert.id} alert={alert} isDismissed />)
              ) : (
                <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
                  <Icon icon='bell' className='w-10 h-10 text-default-400' />
                  <p className='text-small text-default-400'>No dismissed alerts</p>
                </div>
              )}
            </ScrollShadow>
          </CardContent>
          <CardFooter className='justify-end gap-2 px-4'>
            <Button variant='ghost'>Settings</Button>
            {selectedTab === 'new' && newAlerts.length > 0 && (
              <Button
                variant='ghost'
                onPress={() => {
                  // Dismiss all new alerts
                  newAlerts.forEach((alert) => handleDismiss(alert.id))
                }}
              >
                Dismiss All
              </Button>
            )}
          </CardFooter>
        </Card>
      </Popover.Content>
    </Popover>
  )
}

export function AlertPortal() {
  const dispatch = useDispatch()
  const alerts = useSelector((state: RootState) => state.alerts.alerts)
  const dismissedAlerts = useSelector((state: RootState) => state.alerts.dismissedAlerts || [])

  // Auto-hide alerts with autoHideDuration
  useEffect(() => {
    const timers: NodeJS.Timeout[] = []

    alerts.forEach((alert: Alert) => {
      if (alert.autoHideDuration) {
        const timer = setTimeout(() => {
          dispatch(dismissAlert(alert.id))
        }, alert.autoHideDuration)

        timers.push(timer)
      }
    })

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [alerts, dispatch])

  return (
    <div className='flex'>
      <AlertPortalButton />
    </div>
  )
}

// Global alerts display outside the dropdown (for critical alerts). Mounted
// once at the root (providers.tsx), so it renders above whichever route is
// current. The theme scope sits on <html>, so it needs no data-theme wrapper.
export function GlobalAlertsDisplay() {
  const dispatch = useDispatch()
  const alerts = useSelector((state: RootState) => state.alerts.alerts)
  const dismissedAlerts = useSelector((state: RootState) => state.alerts.dismissedAlerts || [])

  // Filter critical alerts that should be displayed globally
  const criticalAlerts = alerts.filter(
    (alert: Alert) =>
      (alert.severity === 'error' || alert.severity === 'warning') && !dismissedAlerts.includes(alert.id)
  )

  // Handle dismiss
  const handleDismiss = async (alertId: string) => {
    // Remove from Redux store
    dispatch(dismissAlert(alertId))

    // Record dismissal on server
    try {
      await dismissGlobalAlert(alertId)
    } catch (error) {
      console.error('Failed to record alert dismissal:', error)
    }
  }

  if (criticalAlerts.length === 0) return null

  return (
    <div
      className='fixed right-4 z-[1050] flex flex-col gap-2 max-w-md'
      style={{ top: 'calc(var(--app-header-h, 64px) + 8px)' }}
    >
      {criticalAlerts.map((alert: Alert) => (
        <AlertDisplay key={alert.id} alert={alert} onDismiss={handleDismiss} isStandalone />
      ))}
    </div>
  )
}
